import React from "react";
import styled from "styled-components";
import ThinkingIndicator from "./ThinkingIndicator";
import SkeletonLoader from "./SkeletonLoader";
import StreamingText from "./StreamingText";
import DataTable from "./DataTable";
import ChartBlock from "./ChartBlock";
import SparkleIcon from "../../../global/chatbot/SparkleIcon";

// ─── Styled Components ────────────────────────────────────────────────────────

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  margin-bottom: 20px;
`;

const AvatarRow = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`;

const Avatar = styled.div`
  width: 30px;
  height: 30px;
  min-width: 30px;
  border-radius: 50%;
  background: linear-gradient(135deg, #cd040b 0%, #ea580c 100%);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const AssistantLabel = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: #1a1a1a;
  letter-spacing: 0.01em;
`;

const ContentArea = styled.div`
  margin-left: 40px;
  margin-right: 0;
  padding-top: 4px;
`;

const ErrorText = styled.p`
  font-size: 15px;
  color: #ef4444;
  margin: 0;
  padding: 6px 0;
`;

const WarningBanner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  background: #fffbeb;
  border: 1px solid #fcd34d;
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 14px;
  color: #92400e;
  margin-top: 8px;
`;

// ─── Component ────────────────────────────────────────────────────────────────

export default function AssistantMessage({ streamingMessage }) {
  const {
    processingState,
    content,
    contentBlocks = [],
    isStreaming,
    hasError,
    errorMessage,
    statusLabel,
    warnings = [],
  } = streamingMessage;

  return (
    <Wrapper>
      <AvatarRow>
        <Avatar>
          <SparkleIcon />
        </Avatar>
        <AssistantLabel>Video Insights Assistant</AssistantLabel>
      </AvatarRow>

      <ContentArea>
        {/* Error state */}
        {hasError && (
          <ErrorText data-testid="assistant-error">
            {errorMessage || "The assistant encountered an error. Please try again."}
          </ErrorText>
        )}

        {/* Thinking phase */}
        {!hasError && processingState === "thinking" && (
          <ThinkingIndicator label={statusLabel} />
        )}

        {/* Responding phase with no content yet */}
        {!hasError && processingState === "responding" && content === "" && (
          <SkeletonLoader />
        )}

        {/* Content available */}
        {!hasError && content !== "" && (
          <StreamingText content={content} isStreaming={isStreaming} />
        )}

        {/* Content blocks from response.table / response.chart SSE events */}
        {!hasError &&
          contentBlocks.map((block, idx) => {
            if (block.type === "table") {
              return block.format === "markdown" ? (
                <StreamingText
                  key={idx}
                  content={block.markdown}
                  isStreaming={false}
                />
              ) : (
                <DataTable
                  key={idx}
                  title={block.title}
                  columns={block.columns}
                  rows={block.rows}
                  totalRows={block.totalRows}
                />
              );
            }
            if (block.type === "chart") {
              return <ChartBlock key={idx} chartSpec={block.chartSpec} />;
            }
            return null;
          })}

        {/* Warning banners */}
        {!hasError &&
          warnings.map((w, i) => (
            <WarningBanner key={i} data-testid="warning-banner">
              {w}
            </WarningBanner>
          ))}
      </ContentArea>
    </Wrapper>
  );
}
